import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Segment, Statistic, Table, Message, Loader, Dropdown, Icon, Button } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import MultiplyHeader from '../components/MultiplyHeader/MultiplyHeader';
import { componentContainerStyle, componentContentStyle } from '../constants';

class MeetingsDashboardPage extends Component {
  state = {
    statusFilter: 'all'
  };

  componentDidMount() {
    this.loadData();
    // Auto-refresh every 30 seconds
    this.interval = setInterval(() => this.loadData(), 30000);
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  loadData = () => {
    const { meetingsStore } = this.props;
    const { statusFilter } = this.state;
    
    const filters = statusFilter === 'all' ? {} : { status: statusFilter };
    meetingsStore.loadMeetings(filters);
  };

  handleStatusFilterChange = (e, { value }) => {
    this.setState({ statusFilter: value }, () => {
      this.loadData();
    });
  };

  formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'blue';
      case 'in_progress': return 'yellow';
      case 'completed': return 'green';
      case 'delivered': return 'green';
      case 'analyzing': return 'purple';
      case 'assigned': return 'teal';
      // Error statuses - use orange (retriable) vs red (permanent)
      case 'analysis_failed': return 'orange';
      case 'assignment_failed': return 'orange';
      case 'download_failed': return 'orange';
      case 'transcription_failed': return 'orange';
      case 'identification_failed': return 'orange';
      case 'fatal': return 'red';
      case 'failed': return 'red';
      default: return 'grey';
    }
  };

  getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return 'clock outline';
      case 'in_progress': return 'spinner';
      case 'completed': return 'check circle';
      case 'delivered': return 'check circle';
      case 'analyzing': return 'lightbulb';
      case 'assigned': return 'user';
      // Error statuses
      case 'analysis_failed': return 'exclamation triangle';
      case 'assignment_failed': return 'exclamation triangle';
      case 'download_failed': return 'exclamation triangle';
      case 'transcription_failed': return 'exclamation triangle';
      case 'identification_failed': return 'exclamation triangle';
      case 'fatal': return 'times circle';
      case 'failed': return 'times circle';
      default: return 'question circle';
    }
  };

  getStatusLabel = (status) => {
    switch (status) {
      case 'analysis_failed': return 'Analysis Failed (Retriable)';
      case 'assignment_failed': return 'Assignment Failed (Retriable)';
      case 'download_failed': return 'Download Failed (Retriable)';
      case 'transcription_failed': return 'Transcription Failed (Retriable)';
      case 'identification_failed': return 'Identification Failed (Retriable)';
      case 'fatal': return 'Fatal Error';
      default: return status;
    }
  };

  renderParticipants = (participants = []) => {
    if (!participants || participants.length === 0) return '-';
    return participants.join(', ');
  };

  renderCreatedVia = (meeting) => {
    if (meeting.approvalRequestId) {
      return <Icon name="check square" title="Via approval request" />;
    }
    return <Icon name="calendar check" title="Direct from calendar" />;
  };

  getMeetingId = (meeting) => {
    return meeting.id || meeting.botId;
  };

  getViewDetailsLink = (meeting) => {
    const meetingId = this.getMeetingId(meeting);
    if (!meetingId) return null;

    // Link to dedicated trace page
    return `/murphy/trace/${meetingId}`;
  };

  render() {
    const { meetingsStore } = this.props;
    const { loading, error, meetings, statistics } = meetingsStore;
    const { statusFilter } = this.state;

    const statusOptions = [
      { key: 'all', text: 'All Status', value: 'all' },
      { key: 'scheduled', text: 'Scheduled', value: 'scheduled' },
      { key: 'in_progress', text: 'In Progress', value: 'in_progress' },
      { key: 'completed', text: 'Completed', value: 'completed' },
      { key: 'failed', text: 'Failed', value: 'failed' }
    ];

    return (
      <div style={componentContainerStyle}>
        <MultiplyHeader header="Murphy Meetings Dashboard" />
        
        <div style={componentContentStyle}>
          {/* Statistics */}
          <Segment>
            <Statistic.Group widths='five' size='small'>
              <Statistic>
                <Statistic.Value>{statistics.total || 0}</Statistic.Value>
                <Statistic.Label>Total</Statistic.Label>
              </Statistic>
              <Statistic color='blue'>
                <Statistic.Value>{statistics.scheduled || 0}</Statistic.Value>
                <Statistic.Label>Scheduled</Statistic.Label>
              </Statistic>
              <Statistic color='yellow'>
                <Statistic.Value>{statistics.inProgress || 0}</Statistic.Value>
                <Statistic.Label>In Progress</Statistic.Label>
              </Statistic>
              <Statistic color='green'>
                <Statistic.Value>{statistics.completed || 0}</Statistic.Value>
                <Statistic.Label>Completed</Statistic.Label>
              </Statistic>
              <Statistic color='red'>
                <Statistic.Value>{statistics.failed || 0}</Statistic.Value>
                <Statistic.Label>Failed</Statistic.Label>
              </Statistic>
            </Statistic.Group>
          </Segment>

          {/* Filters */}
          <Segment>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <strong>Filter by Status:</strong>
              <Dropdown
                selection
                options={statusOptions}
                value={statusFilter}
                onChange={this.handleStatusFilterChange}
              />
            </div>
          </Segment>

          {/* Error Message */}
          {error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          {/* Meetings Table */}
          <Segment>
            {loading ? (
              <Loader active inline='centered' content='Loading meetings...' />
            ) : (
              <Table celled striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Meeting Title</Table.HeaderCell>
                    <Table.HeaderCell>Scheduled Start</Table.HeaderCell>
                    <Table.HeaderCell>Participants</Table.HeaderCell>
                    <Table.HeaderCell>Bot ID</Table.HeaderCell>
                    <Table.HeaderCell>Created Via</Table.HeaderCell>
                    <Table.HeaderCell>Actions</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {meetings.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan='7' textAlign='center'>
                        No meetings found
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    meetings.map(meeting => {
                      const meetingId = this.getMeetingId(meeting);
                      const detailsLink = this.getViewDetailsLink(meeting);
                      
                      return (
                        <Table.Row key={meetingId}>
                          <Table.Cell>
                            <Icon
                              name={this.getStatusIcon(meeting.status)}
                              color={this.getStatusColor(meeting.status)}
                              loading={meeting.status === 'in_progress'}
                            />
                            {this.getStatusLabel(meeting.status)}
                          </Table.Cell>
                          <Table.Cell>
                            {meeting.meetingTitle || '-'}
                            {meeting.meetingUrl && (
                              <div style={{ marginTop: '4px' }}>
                                <a href={meeting.meetingUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85em' }}>
                                  <Icon name='external' size='small' /> Join URL
                                </a>
                              </div>
                            )}
                          </Table.Cell>
                          <Table.Cell>{this.formatTime(meeting.scheduledStartTime)}</Table.Cell>
                          <Table.Cell>{this.renderParticipants(meeting.validatedParticipants)}</Table.Cell>
                          <Table.Cell>
                            {meetingId ? (
                              <code style={{ fontSize: '0.85em' }}>
                                {meetingId.length > 20 ? `${meetingId.substring(0, 20)}...` : meetingId}
                              </code>
                            ) : '-'}
                            {meeting.recallBotId && (
                              <div style={{ fontSize: '0.75em', color: '#888', marginTop: '2px' }}>
                                Recall: {meeting.recallBotId.substring(0, 12)}...
                              </div>
                            )}
                          </Table.Cell>
                          <Table.Cell>{this.renderCreatedVia(meeting)}</Table.Cell>
                          <Table.Cell>
                            {detailsLink ? (
                              <Button
                                as={Link}
                                to={detailsLink}
                                size='small'
                                basic
                                icon
                                labelPosition='left'
                              >
                                <Icon name='search' />
                                Trace
                              </Button>
                            ) : (
                              <span style={{ color: '#999' }}>-</span>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      );
                    })
                  )}
                </Table.Body>
              </Table>
            )}
          </Segment>
        </div>
      </div>
    );
  }
}

export default observer(MeetingsDashboardPage);
